import {
  getFirestore,
  doc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  increment,
  arrayUnion,
  arrayRemove,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import Database from '../types/Database'
import { haversineDistance } from '../utils/distanceUtils'
import { useTestDataStore } from '../stores/testDataStore'

// --- Error types ---

export type JoinRequestErrorCode =
  | 'AUTH_REQUIRED'
  | 'ACCREDITATION_REQUIRED'
  | 'HOST_CANNOT_JOIN'
  | 'DUPLICATE_REQUEST'
  | 'DIVE_NOT_OPEN'
  | 'DIVE_NOT_FOUND'
  | 'CAPACITY_EXCEEDED'
  | 'NETWORK_ERROR'
  | 'PERMISSION_ERROR'
  | 'INVALID_TRANSITION'
  | 'REQUEST_NOT_FOUND'
  | 'REQUEST_NOT_PENDING'

export class JoinRequestError extends Error {
  constructor(public code: JoinRequestErrorCode, message: string) {
    super(message)
    this.name = 'JoinRequestError'
  }
}

// --- Result types ---

export type JoinRequestResult =
  | { status: 'pending' }
  | { status: 'auto-approved'; reason: 'perfect-rating' | 'allowlist' }
  | { error: string }

export type AlwaysApproveResult =
  | { approved: true; addedToAllowlist: true }
  | { approved: false; addedToAllowlist: true; reason: 'at-capacity' }

// --- Constants ---

const PAYMENT_DEADLINE_HOURS = 12

// --- Service functions ---

export async function submitRequest(
  diveId: string
): Promise<JoinRequestResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    return { error: 'Please sign in to continue' }
  }

  const userId = currentUser.uid
  const db = getFirestore()
  let diveBecomeFull = false

  try {
    const result = await runTransaction(db, async transaction => {
      // 1. Read the dive document
      const diveRef = doc(db, 'dives', diveId)
      const diveSnap = await transaction.get(diveRef)

      if (!diveSnap.exists()) {
        return { error: 'Dive not found' } as JoinRequestResult
      }

      const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

      // 2. Validate dive is open
      if (dive.status !== 'open') {
        return {
          error: 'This dive is not accepting new requests'
        } as JoinRequestResult
      }

      // 3. Validate user is not the host
      if (dive.hostId === userId) {
        return { error: 'You cannot join your own dive' } as JoinRequestResult
      }

      // 4. Read requester's user document (accreditation check + perfectRatingCount)
      const requesterRef = doc(db, 'users', userId)
      const requesterSnap = await transaction.get(requesterRef)

      if (!requesterSnap.exists()) {
        return { error: 'User profile not found' } as JoinRequestResult
      }

      const requester = requesterSnap.data() as Omit<Database.User, 'id'>

      // 5. Validate accreditation is verified
      if (
        !requester.accreditation ||
        requester.accreditation.status !== 'verified'
      ) {
        return {
          error: 'Verified accreditation is required to join a dive'
        } as JoinRequestResult
      }

      // 6. Check for duplicate request
      const joinRequestsRef = collection(db, 'joinRequests')
      const duplicateQuery = query(
        joinRequestsRef,
        where('diveId', '==', diveId),
        where('requesterId', '==', userId),
        where('status', 'in', ['pending', 'approved'])
      )
      const duplicateSnap = await getDocs(duplicateQuery)

      if (!duplicateSnap.empty) {
        return {
          error: 'You already have a pending or approved request for this dive'
        } as JoinRequestResult
      }

      // 7. Read host's user document (for allowlist check)
      const hostRef = doc(db, 'users', dive.hostId)
      const hostSnap = await transaction.get(hostRef)
      const host = hostSnap.exists()
        ? (hostSnap.data() as Omit<Database.User, 'id'>)
        : null

      // 8. Count current approved requests to determine capacity
      const approvedQuery = query(
        joinRequestsRef,
        where('diveId', '==', diveId),
        where('status', '==', 'approved')
      )
      const approvedSnap = await getDocs(approvedQuery)
      const approvedCount = approvedSnap.size

      const capacityCap = dive.maxParticipants

      // 9. Evaluate auto-approval rules
      let autoApproveReason: 'perfect-rating' | 'allowlist' | null = null

      // Check perfect rating count first (>= 10)
      const perfectRatingCount = requester.perfectRatingCount ?? 0
      if (perfectRatingCount >= 10) {
        autoApproveReason = 'perfect-rating'
      }

      // Then check allowlist (only if not already flagged for auto-approval)
      if (!autoApproveReason && host?.allowlist?.includes(userId)) {
        autoApproveReason = 'allowlist'
      }

      // 10. Capacity guard for auto-approval
      if (autoApproveReason && approvedCount >= capacityCap) {
        // Cannot auto-approve — would exceed capacity; leave as pending
        autoApproveReason = null
      }

      // 11. Create the join request document
      const now = Timestamp.now()
      const isGuided = dive.type === 'guided'

      if (autoApproveReason) {
        // Auto-approved path
        const paymentDeadline = isGuided
          ? Timestamp.fromMillis(
              now.toMillis() + PAYMENT_DEADLINE_HOURS * 60 * 60 * 1000
            )
          : null

        const newRequestRef = doc(collection(db, 'joinRequests'))
        transaction.set(newRequestRef, {
          diveId,
          requesterId: userId,
          status: 'approved',
          paymentStatus: isGuided ? 'awaiting' : null,
          paymentDeadline,
          createdAt: serverTimestamp()
        })

        // Check if this approval fills the dive to capacity
        if (approvedCount + 1 >= capacityCap) {
          transaction.update(diveRef, { status: 'full' })
          diveBecomeFull = true
        }

        return {
          status: 'auto-approved',
          reason: autoApproveReason
        } as JoinRequestResult
      } else {
        // Pending path
        const newRequestRef = doc(collection(db, 'joinRequests'))
        transaction.set(newRequestRef, {
          diveId,
          requesterId: userId,
          status: 'pending',
          paymentStatus: null,
          paymentDeadline: null,
          createdAt: serverTimestamp()
        })

        // Increment pendingRequestCount on the dive document
        transaction.update(diveRef, { pendingRequestCount: increment(1) })

        return { status: 'pending' } as JoinRequestResult
      }
    })

    // Post-commit: trigger full-dive notification if the dive just became full via auto-approval
    if (diveBecomeFull) {
      triggerFullDiveNotification(diveId).catch(() => {
        // Fire-and-forget — notification delivery is handled downstream
      })
    }

    return result
  } catch (error) {
    if (error instanceof JoinRequestError) {
      return { error: error.message }
    }
    return { error: 'Failed to submit join request. Please try again.' }
  }
}

// --- Status transition validation ---

type StatusTransition = {
  from: Database.JoinRequestStatus
  to: Database.JoinRequestStatus
}

const ALLOWED_TRANSITIONS: StatusTransition[] = [
  { from: 'pending', to: 'approved' },
  { from: 'pending', to: 'declined' },
  { from: 'approved', to: 'declined' },
  { from: 'approved', to: 'forfeited' }
]

export function isValidTransition(
  from: Database.JoinRequestStatus,
  to: Database.JoinRequestStatus
): boolean {
  return ALLOWED_TRANSITIONS.some(t => t.from === from && t.to === to)
}

// --- Host approval ---

export async function approveRequest(requestId: string): Promise<void> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  let diveBecomeFull = false
  let diveId = ''

  await runTransaction(db, async transaction => {
    // 1. Read the join request document
    const requestRef = doc(db, 'joinRequests', requestId)
    const requestSnap = await transaction.get(requestRef)

    if (!requestSnap.exists()) {
      throw new JoinRequestError('REQUEST_NOT_FOUND', 'Join request not found')
    }

    const request = requestSnap.data() as Omit<Database.JoinRequest, 'id'>
    diveId = request.diveId

    // 2. Validate status transition
    if (!isValidTransition(request.status, 'approved')) {
      throw new JoinRequestError(
        'INVALID_TRANSITION',
        'This request is no longer pending'
      )
    }

    // 3. Read the dive document to verify host ownership
    const diveRef = doc(db, 'dives', request.diveId)
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 4. Verify current user is the host
    if (dive.hostId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'You do not have permission to manage requests for this dive'
      )
    }

    // 5. Count current approved requests for capacity check
    const joinRequestsRef = collection(db, 'joinRequests')
    const approvedQuery = query(
      joinRequestsRef,
      where('diveId', '==', request.diveId),
      where('status', '==', 'approved')
    )
    const approvedSnap = await getDocs(approvedQuery)
    const approvedCount = approvedSnap.size

    const capacityCap = dive.maxParticipants

    // 6. Check capacity
    if (approvedCount >= capacityCap) {
      throw new JoinRequestError(
        'CAPACITY_EXCEEDED',
        'This dive has reached maximum capacity'
      )
    }

    // 7. Update request status to approved
    const now = Timestamp.now()
    const isGuided = dive.type === 'guided'
    const paymentDeadline = isGuided
      ? Timestamp.fromMillis(
          now.toMillis() + PAYMENT_DEADLINE_HOURS * 60 * 60 * 1000
        )
      : null

    transaction.update(requestRef, {
      status: 'approved',
      paymentStatus: isGuided ? 'awaiting' : null,
      paymentDeadline
    })

    // 8. Decrement pendingRequestCount (floor at 0)
    const currentPendingCount = dive.pendingRequestCount ?? 0
    transaction.update(diveRef, {
      pendingRequestCount: Math.max(0, currentPendingCount - 1)
    })

    // 9. Set dive to "full" if capacity reached
    if (approvedCount + 1 >= capacityCap) {
      transaction.update(diveRef, { status: 'full' })
      diveBecomeFull = true
    }
  })

  // Post-commit: trigger full-dive notification if the dive just became full
  if (diveBecomeFull) {
    triggerFullDiveNotification(diveId).catch(() => {
      // Fire-and-forget — notification delivery is handled downstream
    })
  }
}

// --- Host decline ---

export async function declineRequest(requestId: string): Promise<void> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()

  await runTransaction(db, async transaction => {
    // 1. Read the join request document
    const requestRef = doc(db, 'joinRequests', requestId)
    const requestSnap = await transaction.get(requestRef)

    if (!requestSnap.exists()) {
      throw new JoinRequestError('REQUEST_NOT_FOUND', 'Join request not found')
    }

    const request = requestSnap.data() as Omit<Database.JoinRequest, 'id'>

    // 2. Validate status transition
    if (!isValidTransition(request.status, 'declined')) {
      throw new JoinRequestError(
        'INVALID_TRANSITION',
        'This request cannot be declined in its current state'
      )
    }

    // 3. If approved, only allow decline when payment is NOT 'paid'
    if (request.status === 'approved' && request.paymentStatus === 'paid') {
      throw new JoinRequestError(
        'INVALID_TRANSITION',
        'Cannot reject a diver who has already paid'
      )
    }

    // 4. Read the dive document to verify host ownership
    const diveRef = doc(db, 'dives', request.diveId)
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 5. Verify current user is the host
    if (dive.hostId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'You do not have permission to manage requests for this dive'
      )
    }

    // 6. Update request status to declined
    transaction.update(requestRef, { status: 'declined' })

    // 7. Decrement pendingRequestCount only if was pending (floor at 0)
    if (request.status === 'pending') {
      const currentPendingCount = dive.pendingRequestCount ?? 0
      transaction.update(diveRef, {
        pendingRequestCount: Math.max(0, currentPendingCount - 1)
      })
    }

    // 8. If was approved and dive was full, reopen it
    if (request.status === 'approved' && dive.status === 'full') {
      transaction.update(diveRef, { status: 'open' })
    }
  })
}

// --- Always Approve (approve + add to allowlist) ---

export async function alwaysApprove(
  requestId: string
): Promise<AlwaysApproveResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  let diveBecomeFull = false
  let diveId = ''

  const result = await runTransaction(db, async transaction => {
    // 1. Read the join request document
    const requestRef = doc(db, 'joinRequests', requestId)
    const requestSnap = await transaction.get(requestRef)

    if (!requestSnap.exists()) {
      throw new JoinRequestError('REQUEST_NOT_FOUND', 'Join request not found')
    }

    const request = requestSnap.data() as Omit<Database.JoinRequest, 'id'>
    diveId = request.diveId

    // 2. Validate request is pending
    if (!isValidTransition(request.status, 'approved')) {
      throw new JoinRequestError(
        'INVALID_TRANSITION',
        'This request is no longer pending'
      )
    }

    // 3. Read the dive document to verify host ownership
    const diveRef = doc(db, 'dives', request.diveId)
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 4. Verify current user is the host
    if (dive.hostId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'You do not have permission to manage requests for this dive'
      )
    }

    // 5. Always add requester to allowlist (regardless of capacity)
    const hostRef = doc(db, 'users', currentUser.uid)
    transaction.update(hostRef, {
      allowlist: arrayUnion(request.requesterId)
    })

    // 6. Count current approved requests for capacity check
    const joinRequestsRef = collection(db, 'joinRequests')
    const approvedQuery = query(
      joinRequestsRef,
      where('diveId', '==', request.diveId),
      where('status', '==', 'approved')
    )
    const approvedSnap = await getDocs(approvedQuery)
    const approvedCount = approvedSnap.size

    const capacityCap = dive.maxParticipants

    // 7. If at capacity, add to allowlist but don't approve
    if (approvedCount >= capacityCap) {
      return {
        approved: false,
        addedToAllowlist: true,
        reason: 'at-capacity'
      } as AlwaysApproveResult
    }

    // 8. Approve the request
    const now = Timestamp.now()
    const isGuided = dive.type === 'guided'
    const paymentDeadline = isGuided
      ? Timestamp.fromMillis(
          now.toMillis() + PAYMENT_DEADLINE_HOURS * 60 * 60 * 1000
        )
      : null

    transaction.update(requestRef, {
      status: 'approved',
      paymentStatus: isGuided ? 'awaiting' : null,
      paymentDeadline
    })

    // 9. Decrement pendingRequestCount (floor at 0)
    const currentPendingCount = dive.pendingRequestCount ?? 0
    transaction.update(diveRef, {
      pendingRequestCount: Math.max(0, currentPendingCount - 1)
    })

    // 10. Set dive to "full" if capacity reached
    if (approvedCount + 1 >= capacityCap) {
      transaction.update(diveRef, { status: 'full' })
      diveBecomeFull = true
    }

    return {
      approved: true,
      addedToAllowlist: true
    } as AlwaysApproveResult
  })

  // Post-commit: trigger full-dive notification if the dive just became full
  if (diveBecomeFull) {
    triggerFullDiveNotification(diveId).catch(() => {
      // Fire-and-forget — notification delivery is handled downstream
    })
  }

  return result
}

// --- Notification event interface ---

export interface NotificationEvent {
  diveId: string
  recipientIds: string[]
  eventType: 'backfill_available' | 'dive_full'
}

// --- Full-dive notification trigger ---

/**
 * Triggers a notification event when a dive transitions to "full" status.
 * Identifies all requesters with status "pending" or "declined" and returns
 * the notification event payload for downstream consumption.
 *
 * De-duplication: This function should only be called at the point where we
 * actually SET the dive status to "full" (i.e., the caller handles ensuring
 * it fires only once per transition).
 *
 * NOTE: Actual notification delivery is handled by a downstream notifications spec.
 * This function only defines the trigger event and recipient list.
 */
export async function triggerFullDiveNotification(
  diveId: string
): Promise<NotificationEvent | null> {
  const db = getFirestore()
  const joinRequestsRef = collection(db, 'joinRequests')

  // Query all requesters with status "pending" or "declined" for this dive
  const pendingDeclinedQuery = query(
    joinRequestsRef,
    where('diveId', '==', diveId),
    where('status', 'in', ['pending', 'declined'])
  )
  const snap = await getDocs(pendingDeclinedQuery)

  // If no pending/declined requesters exist, skip notification
  if (snap.empty) {
    return null
  }

  // Collect all recipient IDs
  const recipientIds: string[] = []
  snap.forEach(docSnap => {
    const data = docSnap.data() as Omit<Database.JoinRequest, 'id'>
    recipientIds.push(data.requesterId)
  })

  return {
    diveId,
    recipientIds,
    eventType: 'dive_full'
  }
}

// --- Forfeit and backfill result types ---

export type ForfeitResult = {
  backfillAvailable: boolean
}

export type BackfillResult = {
  notifiedCount: number
  message: string
}

// --- Forfeit request ---

export async function forfeitRequest(
  requestId: string
): Promise<ForfeitResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()

  const result = await runTransaction(db, async transaction => {
    // 1. Read the join request document
    const requestRef = doc(db, 'joinRequests', requestId)
    const requestSnap = await transaction.get(requestRef)

    if (!requestSnap.exists()) {
      throw new JoinRequestError('REQUEST_NOT_FOUND', 'Join request not found')
    }

    const request = requestSnap.data() as Omit<Database.JoinRequest, 'id'>

    // 2. Validate that the current user is the requester (only the approved person can forfeit)
    if (request.requesterId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'Only the approved requester can forfeit their spot'
      )
    }

    // 3. Validate status transition (approved → forfeited)
    if (!isValidTransition(request.status, 'forfeited')) {
      throw new JoinRequestError(
        'INVALID_TRANSITION',
        'Only approved requests can be forfeited'
      )
    }

    // 4. Read the dive document
    const diveRef = doc(db, 'dives', request.diveId)
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 5. Set request status to forfeited
    transaction.update(requestRef, { status: 'forfeited' })

    // 6. Determine if a backfill prompt should be shown
    if (dive.status === 'full') {
      // Dive was full — host should be prompted about backfill
      return { backfillAvailable: true } as ForfeitResult
    } else {
      // Dive was not full — set status to open
      transaction.update(diveRef, { status: 'open' })
      return { backfillAvailable: false } as ForfeitResult
    }
  })

  return result
}

// --- Confirm backfill ---

export async function confirmBackfill(
  diveId: string,
  forfeitedCount: number
): Promise<BackfillResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()

  // 1. Read the dive document to verify host ownership
  const diveRef = doc(db, 'dives', diveId)

  const result = await runTransaction(db, async transaction => {
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 2. Verify current user is the host
    if (dive.hostId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'You do not have permission to manage this dive'
      )
    }

    // 3. Set dive status to "open"
    transaction.update(diveRef, { status: 'open' })

    // 4. Query pending requests ordered by createdAt ASC, limited to forfeitedCount
    const joinRequestsRef = collection(db, 'joinRequests')
    const pendingQuery = query(
      joinRequestsRef,
      where('diveId', '==', diveId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
      firestoreLimit(forfeitedCount)
    )
    const pendingSnap = await getDocs(pendingQuery)

    // 5. Handle edge case: no pending requesters remain
    if (pendingSnap.empty) {
      return {
        notifiedCount: 0,
        message: 'No pending requesters available for backfill'
      } as BackfillResult
    }

    // 6. Collect recipient IDs for notification
    const recipientIds: string[] = []
    pendingSnap.forEach(docSnap => {
      const data = docSnap.data() as Omit<Database.JoinRequest, 'id'>
      recipientIds.push(data.requesterId)
    })

    return {
      notifiedCount: recipientIds.length,
      message: `${recipientIds.length} requester${
        recipientIds.length > 1 ? 's' : ''
      } notified about available spot`
    } as BackfillResult
  })

  return result
}

// --- Decline backfill ---

export async function declineBackfill(diveId: string): Promise<void> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()

  await runTransaction(db, async transaction => {
    // 1. Read the dive document to verify host ownership
    const diveRef = doc(db, 'dives', diveId)
    const diveSnap = await transaction.get(diveRef)

    if (!diveSnap.exists()) {
      throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
    }

    const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

    // 2. Verify current user is the host
    if (dive.hostId !== currentUser.uid) {
      throw new JoinRequestError(
        'PERMISSION_ERROR',
        'You do not have permission to manage this dive'
      )
    }

    // 3. Set dive status to "open" without notifying anyone
    transaction.update(diveRef, { status: 'open' })
  })
}

// --- Queries and allowlist management ---

export interface PendingRequestView {
  requestId: string
  requesterId: string
  displayName: string
  accreditationStatus: string
  distanceKm: number | null
  pastDivesTogether: number
  totalDivesCompleted: number
  status: Database.JoinRequestStatus
  paymentStatus: Database.JoinRequestPaymentStatus
  createdAt: Timestamp
}

/** @deprecated Use getDiveRequests instead */
export const getPendingRequests = getDiveRequests

export async function getDiveRequests(
  diveId: string
): Promise<PendingRequestView[]> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()

  // 1. Read the dive document to get host info and dive location
  const diveRef = doc(db, 'dives', diveId)
  const diveSnap = await getDoc(diveRef)

  if (!diveSnap.exists()) {
    throw new JoinRequestError('DIVE_NOT_FOUND', 'Dive not found')
  }

  const dive = diveSnap.data() as Omit<Database.Dive, 'id'>
  const hostId = dive.hostId
  const diveLocation = {
    lat: dive.location.latitude,
    lng: dive.location.longitude
  }

  // 2. Query all join requests for this dive (excluding only forfeited/cancelled)
  const joinRequestsRef = collection(db, 'joinRequests')
  const allRequestsQuery = query(
    joinRequestsRef,
    where('diveId', '==', diveId),
    where('status', 'in', ['pending', 'approved', 'declined'])
  )
  const allRequestsSnap = await getDocs(allRequestsQuery)

  if (allRequestsSnap.empty) {
    return []
  }

  // 3. Get host's approved dive IDs for pastDivesTogether calculation
  const hostApprovedQuery = query(
    joinRequestsRef,
    where('requesterId', '==', hostId),
    where('status', '==', 'approved')
  )
  const hostApprovedSnap = await getDocs(hostApprovedQuery)
  const hostApprovedDiveIds = new Set<string>()
  hostApprovedSnap.forEach(docSnap => {
    const data = docSnap.data()
    hostApprovedDiveIds.add(data.diveId)
  })

  // Also get dives hosted by the host (host is always a participant in their own dives)
  const hostedDivesQuery = query(
    collection(db, 'dives'),
    where('hostId', '==', hostId)
  )
  const hostedDivesSnap = await getDocs(hostedDivesQuery)
  const hostedDiveIds = new Set<string>()
  hostedDivesSnap.forEach(docSnap => {
    hostedDiveIds.add(docSnap.id)
  })

  // Combine: host participated in dives where they had an approved request OR where they are the host
  const hostDiveIds = new Set([...hostApprovedDiveIds, ...hostedDiveIds])

  // 4. Resolve each requester's profile and compute fields
  const results: PendingRequestView[] = []

  for (const requestDoc of allRequestsSnap.docs) {
    const requestData = requestDoc.data() as Omit<Database.JoinRequest, 'id'>
    const requesterId = requestData.requesterId

    // Read requester's user document
    const requesterRef = doc(db, 'users', requesterId)
    const requesterSnap = await getDoc(requesterRef)

    let displayName = 'Unknown User'
    let accreditationStatus = 'unknown'
    let distanceKm: number | null = null

    if (requesterSnap.exists()) {
      const requester = requesterSnap.data() as Omit<Database.User, 'id'>
      displayName = requester.displayName || 'Unknown User'
      accreditationStatus = requester.accreditation?.status ?? 'unknown'

      // Calculate distance if requester has a location
      if (requester.location) {
        const requesterLocation = {
          lat: requester.location.latitude,
          lng: requester.location.longitude
        }
        distanceKm = haversineDistance(requesterLocation, diveLocation)
      }
    }

    // Calculate pastDivesTogether
    const requesterApprovedQuery = query(
      joinRequestsRef,
      where('requesterId', '==', requesterId),
      where('status', '==', 'approved')
    )
    const requesterApprovedSnap = await getDocs(requesterApprovedQuery)
    let pastDivesTogether = 0

    requesterApprovedSnap.forEach(docSnap => {
      const data = docSnap.data()
      if (hostDiveIds.has(data.diveId)) {
        pastDivesTogether++
      }
    })

    // totalDivesCompleted: count all approved requests by this requester
    const totalDivesCompleted = requesterApprovedSnap.size

    results.push({
      requestId: requestDoc.id,
      requesterId,
      displayName,
      accreditationStatus,
      distanceKm,
      pastDivesTogether,
      totalDivesCompleted,
      status: requestData.status,
      paymentStatus: requestData.paymentStatus,
      createdAt: requestData.createdAt
    })
  }

  return results
}

// --- Allowlist management ---

export async function addToAllowlist(userId: string): Promise<void> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  const userRef = doc(db, 'users', currentUser.uid)

  await updateDoc(userRef, {
    allowlist: arrayUnion(userId)
  })
}

export async function removeFromAllowlist(userId: string): Promise<void> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  const userRef = doc(db, 'users', currentUser.uid)

  await updateDoc(userRef, {
    allowlist: arrayRemove(userId)
  })
}

export async function getAllowlist(): Promise<string[]> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  const userRef = doc(db, 'users', currentUser.uid)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    return []
  }

  const user = userSnap.data() as Omit<Database.User, 'id'>
  return user.allowlist ?? []
}

// --- Check user's existing request for a dive ---

export async function getUserRequestForDive(
  diveId: string
): Promise<Database.JoinRequest | null> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  const joinRequestsRef = collection(db, 'joinRequests')
  const userRequestQuery = query(
    joinRequestsRef,
    where('diveId', '==', diveId),
    where('requesterId', '==', currentUser.uid)
  )
  const snap = await getDocs(userRequestQuery)

  if (snap.empty) {
    return null
  }

  const docSnap = snap.docs[0]
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as Database.JoinRequest
}

// --- Approved count helper ---

export async function getApprovedCount(diveId: string): Promise<number> {
  const db = getFirestore()
  const joinRequestsRef = collection(db, 'joinRequests')
  const approvedQuery = query(
    joinRequestsRef,
    where('diveId', '==', diveId),
    where('status', '==', 'approved')
  )
  const snap = await getDocs(approvedQuery)
  return snap.size
}

// --- User's own requests with dive data ---

export interface UserJoinRequestWithDive {
  request: Database.JoinRequest
  dive: Database.Dive & { hostHandle?: string | null }
}

export async function getUserRequests(
  status: Database.JoinRequestStatus
): Promise<UserJoinRequestWithDive[]> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new JoinRequestError('AUTH_REQUIRED', 'Please sign in to continue')
  }

  const db = getFirestore()
  const joinRequestsRef = collection(db, 'joinRequests')
  const q = query(
    joinRequestsRef,
    where('requesterId', '==', currentUser.uid),
    where('status', '==', status)
  )
  const snap = await getDocs(q)

  if (snap.empty) return []

  const results: UserJoinRequestWithDive[] = []

  for (const requestDoc of snap.docs) {
    const request = {
      id: requestDoc.id,
      ...requestDoc.data()
    } as Database.JoinRequest

    const diveRef = doc(db, 'dives', request.diveId)
    const diveSnap = await getDoc(diveRef)

    if (!diveSnap.exists()) continue

    const dive = { id: diveSnap.id, ...diveSnap.data() } as Database.Dive

    // Skip dives whose testData flag doesn't match the current toggle
    const showTestData = useTestDataStore.getState().showTestData
    const isTestDive = (dive as any).testData === true
    if (showTestData !== isTestDive) continue

    let hostHandle: string | null = null
    try {
      const hostRef = doc(db, 'users', dive.hostId)
      const hostSnap = await getDoc(hostRef)
      if (hostSnap.exists()) {
        hostHandle = hostSnap.data().handle ?? null
      }
    } catch {
      // Ignore — handle will be null
    }

    results.push({ request, dive: { ...dive, hostHandle } })
  }

  // Sort by dive dateTime ascending
  results.sort(
    (a, b) =>
      a.dive.dateTime.toDate().getTime() - b.dive.dateTime.toDate().getTime()
  )

  return results
}
